import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, useCachedFetch } from '@structural-climbing/auth'
import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Card,
  CardBody,
  Chip,
  DateField,
  PaginationBar,
  pageOffset,
  Select,
  SelectItem,
  Spinner,
  useTabParam,
} from '@structural-climbing/ui'
import { useGymTimezone } from '../contexts/GymContext'
import { MemberProfileModal } from '../features/members/MemberProfileModal'
import { ActivityDiffTable } from '../features/activity/ActivityChanges'
import { ActionIcon, ActivitySentence } from '../features/activity/ActivitySentence'
import {
  ACTIVITY_TABS,
  CHANGE_FIELDS,
  personName,
  type ActivityEntry,
  type ActivityPage as ActivityPageData,
  type ActivityPeople,
  type ActivityTab,
} from '../features/activity/types'
import { PageHeader } from '../components/PageHeader'
import { GymDialog } from '../components/GymDialog'
import { useIsMobile } from '../lib/useIsMobile'
import { gymLocalDate } from '../lib/gymLocalDate'

// The owner's record of who did what at the desk: every sale, transfer, edit,
// cancellation, freeze and deleted invoice, with what each field was before.
//
// A list of one-line sentences on the left, the full before and after of the
// one picked on the right (full screen on a phone). Read only while the page is
// open: one call per filter change and one for the people in the search
// (costs.md, "Database call volume"). No realtime.

const PAGE_SIZE = 50

/** Kinds of change whose entries can be narrowed by the field that changed. */
const FIELD_FILTER_TABS: readonly ActivityTab[] = ['all', 'edits']

const RANGES = ['today', 'days7', 'days30', 'all', 'custom'] as const
type Range = (typeof RANGES)[number]

/** YYYY-MM-DD shifted by whole days, with no timezone involved. */
function shiftDay(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}

export function ActivityPage() {
  const { t, i18n } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const timezone = useGymTimezone()
  const isMobile = useIsMobile()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [tab, setTab] = useTabParam<ActivityTab>('tab', ACTIVITY_TABS, 'all')
  const [actor, setActor] = useState<string | null>(null)
  const [member, setMember] = useState<string | null>(null)
  const [range, setRange] = useState<Range>('all')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [field, setField] = useState('')
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [profileTarget, setProfileTarget] = useState<string | null>(null)

  // Ranges count calendar days in the gym's zone, today included.
  const today = gymLocalDate(new Date(), timezone)
  const { from, to } = useMemo(() => {
    if (range === 'today') return { from: today, to: today }
    if (range === 'days7') return { from: shiftDay(today, -6), to: today }
    if (range === 'days30') return { from: shiftDay(today, -29), to: today }
    if (range === 'custom') return { from: customFrom, to: customTo }
    return { from: '', to: '' }
  }, [range, today, customFrom, customTo])

  const showFieldFilter = FIELD_FILTER_TABS.includes(tab)
  const query = useMemo(() => {
    const params = new URLSearchParams()
    if (tab !== 'all') params.set('type', tab)
    if (actor) params.set('actor', actor)
    if (member) params.set('member', member)
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    if (field && showFieldFilter) params.set('field', field)
    params.set('limit', String(PAGE_SIZE))
    if (page > 1) params.set('offset', String(pageOffset(page, PAGE_SIZE)))
    return params.toString()
  }, [tab, actor, member, from, to, field, showFieldFilter, page])

  async function getJson<T>(path: string): Promise<T> {
    const token = await getAccessTokenSilently()
    const res = await fetch(`${apiUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } })
    if (!res.ok) throw new Error(`${path} ${res.status}`)
    return (await res.json()) as T
  }

  const list = useCachedFetch<ActivityPageData>(`activity:list:${query}`, () =>
    getJson<ActivityPageData>(`/activity?${query}`),
  )
  // The names in the search change only when someone new appears in the log.
  const people = useCachedFetch<ActivityPeople>(
    'activity:people',
    () => getJson<ActivityPeople>('/activity/people'),
    { ttlMs: 5 * 60_000 },
  )

  /** Any filter change starts again from the first page. */
  function narrow(apply: () => void) {
    apply()
    setPage(1)
  }

  function resetFilters() {
    narrow(() => {
      setActor(null)
      setMember(null)
      setRange('all')
      setCustomFrom('')
      setCustomTo('')
      setField('')
    })
  }

  const hasFilters = !!(actor || member || range !== 'all' || field)
  // Keep the current page on screen while the next one loads, instead of a blank list.
  // Stored when a read lands (React's adjust-state-during-render pattern).
  const [lastData, setLastData] = useState<ActivityPageData | undefined>(undefined)
  if (list.data && list.data !== lastData) setLastData(list.data)
  const shown = list.data ?? lastData
  const entries = shown?.entries ?? []
  const planNames = shown?.plan_names ?? {}
  const unknown = t('activity.unknownPerson')
  // On a wide screen the first change is open until another is picked.
  const selected = entries.find((e) => e.id === selectedId) ?? (isMobile ? undefined : entries[0])

  const actorName = (id: string) =>
    personName(people.data?.actors.find((p) => p.id === id) ?? null, unknown)
  const memberName = (id: string) =>
    personName(people.data?.members.find((p) => p.id === id) ?? null, unknown)

  function formatWhen(iso: string): string {
    const locale = i18n.language.startsWith('ro') ? 'ro-RO' : 'en-GB'
    const time = new Date(iso).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone ?? undefined,
    })
    const day = gymLocalDate(new Date(iso), timezone)
    if (day === today) return t('activity.todayAt', { time })
    if (day === shiftDay(today, -1)) return t('activity.yesterdayAt', { time })
    const date = new Date(iso).toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: timezone ?? undefined,
    })
    return `${date}, ${time}`
  }

  function pick(entry: ActivityEntry) {
    setSelectedId(entry.id)
    if (isMobile) setDetailOpen(true)
  }

  const detail = (entry: ActivityEntry) => (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <ActionIcon action={entry.action} />
        <p className="text-[15px] leading-snug">
          <ActivitySentence entry={entry} planNames={planNames} />
        </p>
      </div>
      <p className="text-xs text-foreground/55">
        {formatWhen(entry.created_at)} · {personName(entry.actor, unknown)}
      </p>
      <ActivityDiffTable changes={entry.changes} planNames={planNames} />
      <div className="flex flex-wrap gap-2">
        {entry.member_user_id && (
          <Button color="primary" size="sm" onPress={() => setProfileTarget(entry.member_user_id)}>
            {t('activity.openMember')}
          </Button>
        )}
        <Button
          size="sm"
          variant="flat"
          onPress={() => {
            setDetailOpen(false)
            narrow(() => setActor(entry.actor_user_id))
          }}
        >
          {t('activity.allBy', { name: personName(entry.actor, unknown) })}
        </Button>
      </div>
    </div>
  )

  return (
    <div className="p-4 sm:p-8">
      <PageHeader title={t('activity.title')} subtitle={t('activity.subtitle')} />

      <div className="mb-4 flex flex-col gap-3">
        {/* One search for both people filters: colleagues and members. */}
        <Autocomplete
          aria-label={t('activity.search')}
          placeholder={t('activity.search')}
          selectedKey={null}
          isLoading={people.loading}
          onSelectionChange={(key) => {
            if (!key) return
            // Auth0 ids contain '|' themselves ("auth0|abc"): split on the first one only.
            const raw = String(key)
            const kind = raw.slice(0, raw.indexOf('|'))
            const id = raw.slice(raw.indexOf('|') + 1)
            narrow(() => (kind === 'actor' ? setActor(id) : setMember(id)))
          }}
        >
          {[
            ...(people.data?.actors ?? []).map((p) => ({ p, kind: 'actor' as const })),
            ...(people.data?.members ?? []).map((p) => ({ p, kind: 'member' as const })),
          ].map(({ p, kind }) => (
            <AutocompleteItem
              key={`${kind}|${p.id}`}
              textValue={personName(p, unknown)}
              description={kind === 'actor' ? t('activity.colleague') : t('activity.memberLabel')}
            >
              {personName(p, unknown)}
            </AutocompleteItem>
          ))}
        </Autocomplete>

        {(actor || member) && (
          <div className="flex flex-wrap gap-2">
            {actor && (
              <Chip onClose={() => narrow(() => setActor(null))} variant="flat" color="primary">
                {t('activity.colleague')}: {actorName(actor)}
              </Chip>
            )}
            {member && (
              <Chip onClose={() => narrow(() => setMember(null))} variant="flat" color="primary">
                {t('activity.memberLabel')}: {memberName(member)}
              </Chip>
            )}
          </div>
        )}

        <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 [scrollbar-width:none] md:flex-wrap">
          {ACTIVITY_TABS.map((key) => (
            <Button
              key={key}
              size="sm"
              radius="full"
              variant={tab === key ? 'solid' : 'flat'}
              color={tab === key ? 'primary' : 'default'}
              className="shrink-0 font-semibold"
              aria-pressed={tab === key}
              onPress={() => narrow(() => setTab(key))}
            >
              {t(`activity.tab_${key}`)}
            </Button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {RANGES.map((key) => (
            <Button
              key={key}
              size="sm"
              radius="full"
              variant={range === key ? 'solid' : 'flat'}
              color={range === key ? 'primary' : 'default'}
              className="shrink-0 font-semibold"
              aria-pressed={range === key}
              onPress={() => narrow(() => setRange(key))}
            >
              {t(`activity.range_${key}`)}
            </Button>
          ))}
          {hasFilters && (
            <Button size="sm" variant="light" onPress={resetFilters}>
              {t('activity.resetFilters')}
            </Button>
          )}
        </div>

        {(range === 'custom' || showFieldFilter) && (
          <div className="grid gap-3 sm:grid-cols-3">
            {range === 'custom' && (
              <>
                <DateField
                  label={t('activity.filterFrom')}
                  value={customFrom}
                  max={customTo || undefined}
                  onValueChange={(v) => narrow(() => setCustomFrom(v))}
                />
                <DateField
                  label={t('activity.filterTo')}
                  value={customTo}
                  min={customFrom || undefined}
                  onValueChange={(v) => narrow(() => setCustomTo(v))}
                />
              </>
            )}
            {showFieldFilter && (
              <Select
                size="sm"
                label={t('activity.filterField')}
                selectedKeys={field ? [field] : []}
                onSelectionChange={(keys) =>
                  narrow(() => setField(keys === 'all' ? '' : String([...keys][0] ?? '')))
                }
              >
                {CHANGE_FIELDS.map((f) => (
                  <SelectItem key={f} textValue={t(`activity.field_${f}`)}>
                    {t(`activity.field_${f}`)}
                  </SelectItem>
                ))}
              </Select>
            )}
          </div>
        )}
      </div>

      {list.error && !list.data ? (
        <Chip color="danger" variant="flat">
          {t('activity.loadError')}
        </Chip>
      ) : list.loading && !shown ? (
        <div className="flex justify-center py-12">
          <Spinner size="sm" />
        </div>
      ) : entries.length === 0 ? (
        <Card shadow="sm">
          <CardBody>
            <p className="py-10 text-center text-sm text-foreground/50">{t('activity.empty')}</p>
          </CardBody>
        </Card>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <Card shadow="sm" className="divide-y divide-divider">
            {entries.map((entry) => {
              const isSelected = !isMobile && selected?.id === entry.id
              return (
                <Button
                  key={entry.id}
                  variant="light"
                  radius="none"
                  aria-current={isSelected}
                  className={`h-auto w-full justify-start gap-3 whitespace-normal px-4 py-3 text-left data-[hover=true]:bg-default-50 ${
                    isSelected
                      ? 'bg-primary-50/60 shadow-[inset_3px_0_0_hsl(var(--heroui-primary))]'
                      : ''
                  }`}
                  onPress={() => pick(entry)}
                >
                  <ActionIcon action={entry.action} />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-[13.5px] leading-snug text-foreground">
                      <ActivitySentence entry={entry} planNames={planNames} />
                    </span>
                    <span className="text-xs text-foreground/55">
                      {formatWhen(entry.created_at)}
                    </span>
                  </span>
                </Button>
              )
            })}
          </Card>

          {!isMobile && (
            <Card shadow="sm" className="lg:sticky lg:top-4">
              <CardBody className="px-5 py-5">
                {selected ? (
                  detail(selected)
                ) : (
                  <p className="text-sm text-foreground/50">{t('activity.pickOne')}</p>
                )}
              </CardBody>
            </Card>
          )}
        </div>
      )}

      <PaginationBar
        className="mt-4"
        page={page}
        pageSize={PAGE_SIZE}
        total={shown?.total ?? 0}
        onPageChange={(next) => {
          setPage(next)
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }}
        isDisabled={list.loading}
        ariaLabel={t('activity.pagesLabel')}
        rangeLabel={(first, last, total) => t('activity.pageRange', { first, last, total })}
      />

      {isMobile && detailOpen && selected && (
        <GymDialog onClose={() => setDetailOpen(false)} showCloseButton>
          <div className="p-5 pt-10">{detail(selected)}</div>
        </GymDialog>
      )}
      {profileTarget && (
        <MemberProfileModal userId={profileTarget} onClose={() => setProfileTarget(null)} />
      )}
    </div>
  )
}
